import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import type { RadioShow } from '@/types';

export type AudioSourceType = 'stream' | 'podcast' | null;

interface AudioContextState {
  // Source type
  sourceType: AudioSourceType;
  isPlaying: boolean;
  volume: number;
  isMuted: boolean;
  
  // Stream-specific
  streamUrl: string | null;
  nowPlaying: RadioShow | null;
  isLive: boolean;
  streamNowPlayingText: string | null;
  streamListeners: number | null;
  streamTrack: {
    title: string;
    artist: string;
    artwork: string;
    artUrl: string;
    album: string;
    duration: number;
    isOnline: boolean;
    nextText: string;
    stationId: number;
    shortcode: string;
    stationName: string;
    listenUrl: string;
    text: string;
    elapsed: number;
    fetchedAt: number;
    listeners: number;
  } | null;
  
  // Podcast-specific (minimal - just audio URL and title)
  currentEpisode: { audioUrl: string; title: string } | null;
  currentTime: number;
  duration: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  playStream: () => void;
  playPodcast: (audioUrl: string, title: string) => void;
  pause: () => void;
  toggle: () => void;
  setVolume: (v: number) => void;
  toggleMute: () => void;
  seek: (time: number) => void;
  clearError: () => void;
  setStreamTrack: (track: {
    title: string;
    artist: string;
    artwork: string;
    artUrl: string;
    album: string;
    duration: number;
    isOnline: boolean;
    nextText: string;
    stationId: number;
    shortcode: string;
    stationName: string;
    listenUrl: string;
    text: string;
    elapsed: number;
    fetchedAt: number;
    listeners: number;
  } | null) => void;
}

const AudioContext = createContext<AudioContextState | null>(null);

const DEFAULT_STREAM_URL = 'https://radio.gayphx.com/listen/gayphx/radio.mp3';
const RADIO_NOW_PLAYING_URL = 'https://radio.gayphx.com/api/nowplaying/gayphx';
const VOLUME_STORAGE_KEY = 'gayphx-player-volume';

export function AudioProvider({ children }: { children: ReactNode }) {
  // Source and playback state
  const [sourceType, setSourceType] = useState<AudioSourceType>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.85);
  const [isMuted, setIsMuted] = useState(false);
  
  // Stream state
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [nowPlaying, setNowPlaying] = useState<RadioShow | null>(null);
  const [isLive, setIsLive] = useState(false);
  const [streamNowPlayingText, setStreamNowPlayingText] = useState<string | null>(null);
  const [streamListeners, setStreamListeners] = useState<number | null>(null);
  const [streamTrack, setStreamTrack] = useState<{
    title: string;
    artist: string;
    artwork: string;
    artUrl: string;
    album: string;
    duration: number;
    isOnline: boolean;
    nextText: string;
    stationId: number;
    shortcode: string;
    stationName: string;
    listenUrl: string;
    text: string;
    elapsed: number;
    fetchedAt: number;
    listeners: number;
  } | null>(null);
  
  // Podcast state (minimal)
  const [currentEpisode, setCurrentEpisode] = useState<{ audioUrl: string; title: string } | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Audio element ref
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const wasPlayingRef = useRef(false);
  
  // Load volume from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(VOLUME_STORAGE_KEY);
      if (saved) {
        const parsed = parseFloat(saved);
        if (!isNaN(parsed) && parsed >= 0 && parsed <= 1) {
          setVolumeState(parsed);
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);
  
  // Save volume to localStorage when changed
  useEffect(() => {
    try {
      localStorage.setItem(VOLUME_STORAGE_KEY, volume.toString());
    } catch {
      // Ignore localStorage errors
    }
  }, [volume]);
  
  // Initialize audio element (run once on mount)
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audioRef.current = audio;
    
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);
    const handleError = () => {
      setIsPlaying(false);
      setIsLoading(false);
      setError('Audio playback failed. Please try again.');
    };
    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    
    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.pause();
    };
  }, []);
  
  // Update volume
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);
  
  // Load stream URL on mount
  useEffect(() => {
    const loadStreamUrl = async () => {
      try {
        const { publicApi } = await import('@/lib/api');
        const response = await publicApi.settings.get();
        const url = response.data.streamUrl;
        setStreamUrl(url && url.trim() ? url.trim() : DEFAULT_STREAM_URL);
      } catch (error) {
        console.error('Failed to load stream URL:', error);
        setStreamUrl(DEFAULT_STREAM_URL);
      }
    };
    loadStreamUrl();
  }, []);
  
  // Poll for now playing info when stream is active
  useEffect(() => {
    if (sourceType !== 'stream') return;
    
    const fetchLineup = async () => {
      try {
        const { publicApi } = await import('@/lib/api');
        const [lineupRes, streamRes] = await Promise.allSettled([
          publicApi.schedule.getLineup(),
          fetch(RADIO_NOW_PLAYING_URL, { cache: 'no-store' }),
        ]);

        if (lineupRes.status === 'fulfilled') {
          setNowPlaying(lineupRes.value.data.current);
          setIsLive(!!lineupRes.value.data.current);
        } else {
          setNowPlaying(null);
          setIsLive(false);
        }

        if (streamRes.status === 'fulfilled' && streamRes.value.ok) {
          const payload = await streamRes.value.json();
          const isOnline = !!payload?.is_online;
          const text = payload?.now_playing?.song?.text as string | undefined;
          const safeText = text && text !== 'Station Offline' ? text : null;
          const listenerCount = payload?.station?.mounts?.[0]?.listeners?.current;

          setStreamNowPlayingText(isOnline ? safeText : null);
          setStreamListeners(
            typeof listenerCount === 'number' && Number.isFinite(listenerCount) ? listenerCount : null
          );
        } else {
          setStreamNowPlayingText(null);
          setStreamListeners(null);
        }
      } catch (error) {
        console.error('Failed to fetch lineup:', error);
        setNowPlaying(null);
        setIsLive(false);
        setStreamNowPlayingText(null);
        setStreamListeners(null);
      }
    };
    
    fetchLineup();
    const interval = setInterval(fetchLineup, 60000);
    return () => clearInterval(interval);
  }, [sourceType]);
  
  // Play stream
  const playStream = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !streamUrl) return;
    // User explicitly clicked play; ensure we are not silently muted.
    if (isMuted) {
      setIsMuted(false);
    }
    if (volume <= 0.001) {
      setVolumeState(0.85);
    }
    audio.muted = false;

    
    setError(null);
    
    // If already playing stream and not paused, do nothing
    if (sourceType === 'stream' && !audio.paused) return;
    
    // If switching to stream, set up the source first
    if (sourceType !== 'stream') {
      audio.pause();
      audio.src = streamUrl;
      audio.preload = 'metadata';
      setSourceType('stream');
      setCurrentEpisode(null);
      setCurrentTime(0);
      setDuration(0);
      // Load before playing
      audio.load();
    }
    
    try {
      await audio.play();
    } catch (err) {
      console.error('Failed to play stream:', err);
      setError('Click play to start the stream');
    }
  }, [sourceType, streamUrl, isMuted, volume]);
  
  // Play podcast
  const playPodcast = useCallback(async (audioUrl: string, title: string) => {
    const audio = audioRef.current;
    if (!audio) return;
    // User explicitly clicked play; ensure we are not silently muted.
    if (isMuted) {
      setIsMuted(false);
    }
    if (volume <= 0.001) {
      setVolumeState(0.85);
    }
    audio.muted = false;

    
    setError(null);
    
    // If same episode, just toggle play/pause
    if (currentEpisode?.audioUrl === audioUrl) {
      if (audio.paused) {
        try {
          await audio.play();
        } catch (err) {
          setError('Click play to resume');
        }
      }
      return;
    }
    
    // Switch to new episode
    audio.pause();
    audio.src = audioUrl;
    audio.preload = 'metadata';
    audio.load();
    
    setSourceType('podcast');
    setCurrentEpisode({ audioUrl, title });
    setCurrentTime(0);
    setDuration(0);
    wasPlayingRef.current = true;
    
    try {
      await audio.play();
    } catch (err) {
      console.error('Failed to play podcast:', err);
      setError('Click play to start the episode');
    }
  }, [currentEpisode, isMuted, volume]);
  
  // Pause
  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
    }
  }, []);
  
  // Toggle play/pause
  const toggle = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (audio.paused) {
      if (!sourceType && streamUrl) {
        // Default to stream if nothing playing
        await playStream();
      } else {
        try {
          await audio.play();
        } catch (err) {
          setError('Click play to resume');
        }
      }
    } else {
      audio.pause();
    }
  }, [sourceType, streamUrl, playStream]);
  
  // Set volume
  const setVolume = useCallback((v: number) => {
    const clamped = Math.max(0, Math.min(1, v));
    setVolumeState(clamped);
    if (clamped > 0 && isMuted) {
      setIsMuted(false);
    }
  }, [isMuted]);
  
  // Toggle mute
  const toggleMute = useCallback(() => {
    setIsMuted(m => !m);
  }, []);
  
  // Seek (podcast only)
  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio || sourceType !== 'podcast') return;
    
    const clamped = Math.max(0, Math.min(duration || 0, time));
    audio.currentTime = clamped;
    setCurrentTime(clamped);
  }, [sourceType, duration]);
  
  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  const value: AudioContextState = {
    sourceType,
    isPlaying,
    volume,
    isMuted,
    streamUrl,
    nowPlaying,
    isLive,
    streamNowPlayingText,
    streamListeners,
    streamTrack,
    currentEpisode,
    currentTime,
    duration,
    isLoading,
    error,
    playStream,
    playPodcast,
    pause,
    toggle,
    setVolume,
    toggleMute,
    seek,
    clearError,
    setStreamTrack,
  };
  
  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio() {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
}
